/**
 * Onboarding join route — redeem an invite code to join an existing org or class.
 *
 * Provides a two-step flow:
 *
 * 1. **Enter code** — the user types or pastes a 9-character invite code
 *    into an OTP-style input (3-3-3 groups with separators). The code is
 *    looked up via `invites.peek` to preview what they're joining.
 * 2. **Confirm** — after reviewing the details the user confirms and the
 *    code is redeemed via `invites.redeem`, creating the appropriate
 *    membership or enrollment.
 *
 * On successful redemption the user's scopes are re-evaluated and they
 * are redirected to the matching zone (`/admin` or `/learn`).
 *
 * @module routes/onboard-join
 */

import { defaultRedirectForScopes, resolveScopes } from '@learnai/api/auth';
import { useAuth } from '@learnai/api/auth-provider';
import { tryCatch } from '@learnai/tools/try-catch';
import { BrandLogo } from '@learnai/ui/components/blocks/brand-logo';
import { Button } from '@learnai/ui/components/ui/button';
import { Card, CardContent } from '@learnai/ui/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@learnai/ui/components/ui/input-otp';
import { toast } from '@learnai/ui/components/ui/sonner';
import { Spinner } from '@learnai/ui/components/ui/spinner';
import { api } from '@web/lib/api';
import { ArrowLeftIcon, CheckIcon, ShieldCheckIcon, TicketIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Total number of characters in an invite code (and class join code). */
const CODE_LENGTH = 9;

/**
 * Regex pattern passed to `InputOTP` to restrict input to alphanumeric
 * characters. Dashes pasted from formatted codes (e.g. `XXX-XXX-XXX`)
 * are automatically stripped by the OTP input's pattern filter.
 */
const ALPHANUMERIC_PATTERN = '^[a-zA-Z0-9]+$';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Shape returned by the `invites.peek` endpoint. */
interface InvitePreview {
	code: string;
	type: 'organization' | 'class';
	targetName: string;
	role: string;
	organizationName: string | null;
	flow: 'invite' | 'class_join';
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Human-readable labels for org and class roles. */
const ROLE_LABELS: Record<string, string> = {
	OWNER: 'Owner',
	ADMIN: 'Admin',
	MEMBER: 'Member',
	INSTRUCTOR: 'Instructor',
	ASSISTANT: 'Assistant',
	STUDENT: 'Student',
	AUDITOR: 'Auditor',
};

/**
 * Safely extracts a `.message` string from an unknown error value.
 * The oRPC client throws plain objects with a `message` field on HTTP
 * errors — this avoids repeating the type assertion at every call site.
 */
function errorMessage(err: unknown): string | undefined {
	return (err as { message?: string })?.message;
}

/** Formats a code into display form: 9 chars → XXX-XXX-XXX. */
function formatCodeForDisplay(code: string): string {
	const normalised = code.replace(/-/g, '');
	if (normalised.length === CODE_LENGTH)
		return `${normalised.slice(0, 3)}-${normalised.slice(3, 6)}-${normalised.slice(6, 9)}`;
	return code;
}

/**
 * After redeeming an invite, re-fetches the user's memberships and
 * enrollments, resolves their scopes, and returns the best redirect
 * destination. Profile-completeness is enforced on entry to this route
 * (see the `useEffect` in `OnboardJoin`), so by the time we get here
 * the user is guaranteed to have a complete profile.
 */
async function resolvePostRedeemScopes(): Promise<string> {
	const [membershipsResult, enrollmentsResult] = await Promise.all([
		tryCatch(api.users.listMemberships()),
		tryCatch(api.users.listEnrollments()),
	]);

	const memberships = membershipsResult.data?.memberships ?? [];
	const enrollments = enrollmentsResult.data?.enrollments ?? [];

	const scopes = resolveScopes(memberships, enrollments);
	return defaultRedirectForScopes(scopes);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OnboardJoin() {
	const { user, profileComplete, isLoading: authLoading } = useAuth();
	const [searchParams] = useSearchParams();

	// ── Step state machine: enter → preview → success ─────────────────
	const [step, setStep] = useState<'enter' | 'preview' | 'success'>('enter');

	// Pre-fill the code from `?code=` query param on first render so
	// deep-links (e.g. `/onboard/join?code=ABCXYZ123`) populate the
	// OTP input immediately without a flash of empty state.
	const [code, setCode] = useState(() => {
		const urlCode = searchParams.get('code');
		return urlCode ? urlCode.toUpperCase() : '';
	});

	// ── Async loading flags ───────────────────────────────────────────
	const [lookingUp, setLookingUp] = useState(false);
	const [preview, setPreview] = useState<InvitePreview | null>(null);
	const [redeeming, setRedeeming] = useState(false);

	/** True while any async operation is in flight — disables inputs. */
	const isBusy = lookingUp || redeeming;

	// -------------------------------------------------------------------
	// Profile gate — invite-link users must finish profile first, but
	// returnTo back here so the original code is preserved.
	// -------------------------------------------------------------------

	useEffect(() => {
		if (authLoading || !user || profileComplete) return;
		const returnTo = `/onboard/join${window.location.search}`;
		window.location.replace(`/onboard/profile?returnTo=${encodeURIComponent(returnTo)}`);
	}, [authLoading, user, profileComplete]);

	// -------------------------------------------------------------------
	// Handlers — lookup
	// -------------------------------------------------------------------

	/**
	 * Peeks at the invite code via the API. On success transitions to
	 * the preview step; on failure shows a descriptive toast.
	 */
	const lookupCode = useCallback(async (value: string) => {
		if (value.length === 0) {
			toast.error('Please enter an invite code.');
			return;
		}

		if (value.length !== CODE_LENGTH) {
			toast.error(`Enter a ${CODE_LENGTH}-character code (e.g. ABC-XYZ-123).`);
			return;
		}

		setLookingUp(true);

		const { data, error } = await tryCatch(api.invites.peek({ code: value }));

		if (error || !data) {
			const msg = errorMessage(error);

			if (msg?.toLowerCase().includes('not found')) {
				toast.error('Invite code not found. Please check the code and try again.');
			} else {
				toast.error(msg ?? 'Unable to look up the invite code. Please check your connection and try again.');
			}

			setLookingUp(false);
			return;
		}

		setPreview(data);
		setStep('preview');
		setLookingUp(false);
	}, []);

	// Auto-trigger lookup when the URL provides a complete code on mount.
	// A ref guards against StrictMode's double-invocation firing two API calls.
	const didAutoLookup = useRef(false);

	useEffect(() => {
		if (!didAutoLookup.current && code.length === CODE_LENGTH) {
			didAutoLookup.current = true;
			lookupCode(code);
		}
	}, [code, lookupCode]);

	/** Form submit handler — prevents default and delegates to `lookupCode`. */
	const handleLookup = useCallback(
		(e?: React.SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
			e?.preventDefault();
			e?.stopPropagation();
			lookupCode(code);
		},
		[code, lookupCode],
	);

	// -------------------------------------------------------------------
	// Handlers — redeem
	// -------------------------------------------------------------------

	/**
	 * Redeems the previewed invite code. On success, transitions to the
	 * success step, resolves the user's new scopes, and performs a
	 * full-page redirect to the appropriate zone.
	 */
	const handleRedeem = useCallback(async () => {
		if (!preview) return;
		setRedeeming(true);

		if (preview.flow === 'class_join') {
			const { error } = await tryCatch(api.enrollments.join({ joinCode: preview.code }));
			if (error) {
				toast.error(errorMessage(error) ?? 'Failed to join the class.');
				setRedeeming(false);
				return;
			}
		} else {
			const { error } = await tryCatch(api.invites.redeem({ code: preview.code }));
			if (error) {
				toast.error(errorMessage(error) ?? 'Failed to redeem the invite code.');
				setRedeeming(false);
				return;
			}
		}

		// Show the success interstitial while we resolve the redirect.
		setStep('success');
		const destination = await resolvePostRedeemScopes();
		window.location.href = destination;
	}, [preview]);

	// -------------------------------------------------------------------
	// Handlers — reset
	// -------------------------------------------------------------------

	/** Returns to the code entry step, clearing all preview state. */
	const handleReset = useCallback(() => {
		setStep('enter');
		setCode('');
		setPreview(null);
	}, []);

	// -------------------------------------------------------------------
	// Render — success interstitial (shown briefly while redirect fires)
	// -------------------------------------------------------------------

	if (step === 'success') {
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<div className="flex flex-col items-center gap-3">
					<div className="flex size-12 items-center justify-center rounded-full bg-success-muted text-success">
						<CheckIcon className="size-6" />
					</div>
					<p className="text-sm font-medium text-foreground">You're in! Redirecting…</p>
					<Spinner className="size-4 text-primary" />
				</div>
			</div>
		);
	}

	// -------------------------------------------------------------------
	// Render — preview / confirmation step
	// -------------------------------------------------------------------

	if (step === 'preview' && preview) {
		const roleLabel = ROLE_LABELS[preview.role] ?? preview.role;
		const targetType = preview.type === 'organization' ? 'organization' : 'class';

		return (
			<div className="relative flex min-h-screen w-full items-center justify-center px-4 py-12">
				<nav className="absolute top-6 left-6">
					<a href="/">
						<BrandLogo size="sm" />
					</a>
				</nav>

				<Card className="w-full max-w-md overflow-hidden">
					<CardContent className="p-6 md:p-8">
						<div className="flex flex-col gap-6">
							{/* ── Back to code entry ─────────────────── */}
							<button
								type="button"
								onClick={handleReset}
								className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
							>
								<ArrowLeftIcon className="size-3.5" />
								Use a different code
							</button>

							{/* ── Header ──────────────────────────────── */}
							<div className="flex flex-col items-center gap-3 text-center">
								<div className="flex size-12 items-center justify-center rounded-full bg-success-muted text-success">
									<ShieldCheckIcon className="size-6" />
								</div>
								<div>
									<h1 className="text-2xl font-bold">Confirm your invitation</h1>
									<p className="mt-1 text-balance text-muted-foreground">
										Review the details below and confirm to join.
									</p>
								</div>
							</div>

							{/* ── Invite details (description list) ──── */}
							<div className="rounded-lg border bg-muted p-4">
								<dl className="space-y-3 text-sm">
									<div className="flex justify-between">
										<dt className="text-muted-foreground">Joining</dt>
										<dd className="font-medium text-foreground">{preview.targetName}</dd>
									</div>
									<div className="flex justify-between">
										<dt className="text-muted-foreground">Type</dt>
										<dd className="capitalize text-foreground">{targetType}</dd>
									</div>

									{/* Show the parent org only for class invites. */}
									{preview.organizationName && preview.type === 'class' && (
										<div className="flex justify-between">
											<dt className="text-muted-foreground">Organization</dt>
											<dd className="text-foreground">{preview.organizationName}</dd>
										</div>
									)}

									<div className="flex justify-between">
										<dt className="text-muted-foreground">Role</dt>
										<dd className="font-medium text-foreground">{roleLabel}</dd>
									</div>
									<div className="flex justify-between">
										<dt className="text-muted-foreground">Invite code</dt>
										<dd className="font-mono text-xs text-foreground">{formatCodeForDisplay(preview.code)}</dd>
									</div>
								</dl>
							</div>

							{/* ── Action buttons ──────────────────────── */}
							<div className="flex flex-col gap-2">
								<Button onClick={handleRedeem} className="w-full" disabled={redeeming}>
									{redeeming ? <Spinner className="size-3" /> : 'Join'}
								</Button>
								<Button variant="ghost" className="w-full" onClick={handleReset} disabled={redeeming}>
									Cancel
								</Button>
							</div>

							{/* ── Signed-in identity hint ────────────── */}
							{user && (
								<p className="text-center text-xs text-muted-foreground">
									You'll join as <span className="font-medium text-foreground">{user.name ?? user.email}</span>
								</p>
							)}
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	// -------------------------------------------------------------------
	// Render — code entry step (default)
	// -------------------------------------------------------------------

	return (
		<div className="relative flex min-h-screen w-full items-center justify-center px-4 py-12">
			<nav className="absolute top-6 left-6">
				<a href="/">
					<BrandLogo size="sm" />
				</a>
			</nav>

			<Card className="w-full max-w-md overflow-hidden">
				<CardContent className="p-6 md:p-8">
					<form onSubmit={handleLookup}>
						<div className="flex flex-col gap-6">
							{/* ── Back to onboarding hub ───────────────── */}
							<div>
								<Link
									to="/onboard"
									className="inline-flex items-center gap-1.5 p-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
								>
									<ArrowLeftIcon className="size-3.5" />
									Back
								</Link>
							</div>

							{/* ── Header ──────────────────────────────── */}
							<div className="flex flex-col items-center gap-3 text-center">
								<div className="flex size-12 items-center justify-center rounded-full bg-success-muted text-success">
									<TicketIcon className="size-6" />
								</div>
								<div>
									<h1 className="text-2xl font-bold">Enter your invite code</h1>
									<p className="mt-1 text-balance text-muted-foreground">
										Paste the code you received from your administrator or instructor.
									</p>
								</div>
							</div>

							{/* ── OTP-style code input (3-3-3 layout) ── */}
							<div className="flex flex-col items-center gap-2">
								<InputOTP
									autoFocus
									maxLength={CODE_LENGTH}
									pattern={ALPHANUMERIC_PATTERN}
									value={code}
									onChange={(value) => setCode(value.toUpperCase())}
									onComplete={(value) => lookupCode(value.toUpperCase())}
									disabled={isBusy}
									containerClassName="justify-center"
								>
									<InputOTPGroup>
										<InputOTPSlot index={0} className="h-14 w-10 text-xl font-semibold bg-background" />
										<InputOTPSlot index={1} className="h-14 w-10 text-xl font-semibold bg-background" />
										<InputOTPSlot index={2} className="h-14 w-10 text-xl font-semibold bg-background" />
									</InputOTPGroup>
									<InputOTPSeparator />
									<InputOTPGroup>
										<InputOTPSlot index={3} className="h-14 w-10 text-xl font-semibold bg-background" />
										<InputOTPSlot index={4} className="h-14 w-10 text-xl font-semibold bg-background" />
										<InputOTPSlot index={5} className="h-14 w-10 text-xl font-semibold bg-background" />
									</InputOTPGroup>
									<InputOTPSeparator />
									<InputOTPGroup>
										<InputOTPSlot index={6} className="h-14 w-10 text-xl font-semibold bg-background" />
										<InputOTPSlot index={7} className="h-14 w-10 text-xl font-semibold bg-background" />
										<InputOTPSlot index={8} className="h-14 w-10 text-xl font-semibold bg-background" />
									</InputOTPGroup>
								</InputOTP>
								<p className="text-xs text-muted-foreground">Ask your administrator if you don't have one.</p>
							</div>

							{/* ── Signed-in identity hint ────────────── */}
							{user && (
								<p className="text-center text-xs text-muted-foreground">
									Signed in as <span className="font-medium text-foreground">{user.email}</span>
								</p>
							)}
						</div>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
